import { z } from "zod";

const classification = z.enum(["new", "update_existing", "skip_no_changes", "error"]);

const importRowReportSchema = z.object({
  rowIndex: z.number().int(),
  classification,
  errorReason: z.string().optional(),
  errorCode: z.string().optional(),
  /// Fields the commit will skip on a row that still imports. The shore
  /// writes them value-free (column and rule only), so they are safe to show.
  warnings: z.array(z.string()).optional(),
  email: z.string().nullable(),
  rawInput: z.record(z.string(), z.string()),
});

export const dryRunResultSchema = z.object({
  importId: z.string(),
  totalRows: z.number().int(),
  classifications: z.object({
    new: z.number().int(),
    update_existing: z.number().int(),
    skip_no_changes: z.number().int(),
    error: z.number().int(),
  }),
  rows: z.array(importRowReportSchema),
  newDepartmentsToCreate: z.array(z.string()),
  cycleWarnings: z.array(z.object({ via: z.array(z.string()) })),
  expiresAt: z.string(),
  /// Handler key ("hr", "payroll", "compensation") to that handler's own
  /// counts. Defaulted because a file no handler claimed may omit it.
  extensionCounts: z.record(z.string(), z.record(z.string(), z.number())).default({}),
});

export const commitResultSchema = z.object({
  importId: z.string(),
  created: z.number().int(),
  updated: z.number().int(),
  errors: z.number().int(),
  status: z.enum(["committed", "failed"]),
});

export const importHistoryEntrySchema = z.object({
  id: z.string(),
  source: z.string(),
  byUserId: z.string(),
  totalRows: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  errors: z.number().int(),
  status: z.string(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
});

export const importHistoryListSchema = z.object({
  rows: z.array(importHistoryEntrySchema),
  nextCursor: z.string().nullable(),
});

export type DryRunResult = z.infer<typeof dryRunResultSchema>;
export type CommitResult = z.infer<typeof commitResultSchema>;
export type ImportHistoryEntry = z.infer<typeof importHistoryEntrySchema>;
export type ImportHistoryList = z.infer<typeof importHistoryListSchema>;
